import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';
import { AppStateService } from './app-state.service';
import { GlobalsService } from './globals.service';
import { Country, State, City } from '../models';
import { AMF0Decoder } from './amf-encoder';
import * as pako from 'pako';

/**
 * Common Service - loads shared data like countries
 * 
 * Countries are loaded from a compressed AMF binary file (countries.db)
 * The file is DEFLATE compressed and contains AMF0/AMF3 serialized data
 */
@Injectable({
  providedIn: 'root'
})
export class CommonService {
  private readonly http = inject(HttpClient);
  private readonly appState = inject(AppStateService);
  private readonly globals = inject(GlobalsService);

  private countriesLoaded = false;
  private readonly amfDecoder = new AMF0Decoder();

  /**
   * Load countries from the countries.db file
   * The file is a DEFLATE compressed AMF serialized array of Country objects
   */
  async loadCountries(): Promise<void> {
    if (this.countriesLoaded && this.appState.countries().length > 0) {
      console.log('[CommonService] Countries already loaded');
      return;
    }

    try {
      console.log('[CommonService] Loading countries from server...');
      const url = `${this.globals.storageUrl}/express/countries.db?nocache=${Date.now()}`;
      
      // Download the compressed file as ArrayBuffer
      const compressedData = await firstValueFrom(
        this.http.get(url, { responseType: 'arraybuffer' })
      );
      
      console.log('[CommonService] Downloaded countries.db, size:', compressedData.byteLength);
      
      // Decompress using pako - use inflateRaw for raw DEFLATE (no zlib header)
      // The legacy app uses CompressionAlgorithm.DEFLATE which is raw DEFLATE
      const decompressed = pako.inflateRaw(new Uint8Array(compressedData));
      
      // Decode AMF3 data (countries.db uses pure AMF3 format, not AMF0)
      const rawCountries = this.amfDecoder.decodeAMF3(decompressed.buffer);
      console.log('[CommonService] Decoded AMF data, countries count:', rawCountries?.length);
      
      // Transform to our Country interface
      const countries = this.transformCountries(rawCountries);
      console.log('[CommonService] Transformed countries:', countries.length);
      
      this.appState.setCountries(countries);
      this.countriesLoaded = true;
      
    } catch (error) {
      console.error('[CommonService] Error loading countries from server:', error);
      console.log('[CommonService] Falling back to static list');
      this.loadFallbackCountries();
    }
  }

  /**
   * Transform raw AMF country data to our Country interface
   */
  private transformCountries(rawData: any[]): Country[] {
    if (!Array.isArray(rawData)) {
      console.warn('[CommonService] Raw data is not an array');
      return [];
    }

    return rawData.map((raw: any) => {
      const country: Country = {
        id: raw.id || raw.ID || 0,
        name: raw.name || raw.Name || '',
        code: raw.code || raw.Code || '',
        // Optional fields from the dreambooks-api alignment — kept undefined
        // when the AMF payload doesn't carry them yet, so getPostalFormat()
        // falls back to its ISO-2 table.
        postalMask: raw.postalMask ?? raw.PostalMask ?? raw.postal_mask ?? undefined,
        isoVies: raw.isoVies ?? raw.IsoVies ?? raw.iso_vies ?? undefined,
        euMember: raw.euMember ?? raw.EuMember ?? raw.eu_member ?? undefined,
        enabled: raw.enabled ?? raw.Enabled ?? undefined,
        states: this.transformStates(raw.states || raw.States || [], raw.id || raw.ID || 0)
      };
      return country;
    }).filter(c => c.id > 0);
  }

  /**
   * Transform raw state data
   */
  private transformStates(rawStates: any[], countryId: number): State[] {
    if (!Array.isArray(rawStates)) return [];

    return rawStates.map((raw: any) => {
      const state: State = {
        id: raw.id || raw.ID || 0,
        name: raw.name || raw.Name || '',
        code: raw.code || raw.Code || '',
        countryId: countryId,
        cities: this.transformCities(raw.cities || raw.Cities || [], raw.id || raw.ID || 0)
      };
      return state;
    }).filter(s => s.id > 0);
  }

  /**
   * Transform raw city data
   */
  private transformCities(rawCities: any[], stateId: number): City[] {
    if (!Array.isArray(rawCities)) return [];

    return rawCities.map((raw: any) => {
      const city: City = {
        id: raw.id || raw.ID || 0,
        name: raw.name || raw.Name || '',
        stateId: stateId
      };
      return city;
    }).filter(c => c.id > 0);
  }

  /**
   * Load fallback countries list when the server data is unavailable
   */
  private loadFallbackCountries(): void {
    console.log('[CommonService] Loading fallback countries');
    
    // This is a subset of common countries with their states/cities
    // In production, this should come from the server
    const fallbackCountries: Country[] = [
      {
        id: 1,
        name: 'Portugal',
        code: 'PT',
        states: [
          { id: 1, name: 'Aveiro', code: 'AVE', countryId: 1, cities: [{ id: 1, name: 'Aveiro', stateId: 1 }, { id: 2, name: 'Águeda', stateId: 1 }] },
          { id: 2, name: 'Beja', code: 'BEJ', countryId: 1, cities: [{ id: 10, name: 'Beja', stateId: 2 }] },
          { id: 3, name: 'Braga', code: 'BRA', countryId: 1, cities: [{ id: 20, name: 'Braga', stateId: 3 }, { id: 21, name: 'Barcelos', stateId: 3 }] },
          { id: 4, name: 'Bragança', code: 'BGC', countryId: 1, cities: [{ id: 30, name: 'Bragança', stateId: 4 }] },
          { id: 5, name: 'Castelo Branco', code: 'CBR', countryId: 1, cities: [{ id: 40, name: 'Castelo Branco', stateId: 5 }] },
          { id: 6, name: 'Coimbra', code: 'COI', countryId: 1, cities: [{ id: 50, name: 'Coimbra', stateId: 6 }] },
          { id: 7, name: 'Évora', code: 'EVO', countryId: 1, cities: [{ id: 60, name: 'Évora', stateId: 7 }] },
          { id: 8, name: 'Faro', code: 'FAR', countryId: 1, cities: [{ id: 70, name: 'Faro', stateId: 8 }, { id: 71, name: 'Albufeira', stateId: 8 }] },
          { id: 9, name: 'Guarda', code: 'GUA', countryId: 1, cities: [{ id: 80, name: 'Guarda', stateId: 9 }] },
          { id: 10, name: 'Leiria', code: 'LEI', countryId: 1, cities: [{ id: 90, name: 'Leiria', stateId: 10 }] },
          { id: 11, name: 'Lisboa', code: 'LIS', countryId: 1, cities: [{ id: 100, name: 'Lisboa', stateId: 11 }, { id: 101, name: 'Sintra', stateId: 11 }, { id: 102, name: 'Cascais', stateId: 11 }] },
          { id: 12, name: 'Portalegre', code: 'PTG', countryId: 1, cities: [{ id: 110, name: 'Portalegre', stateId: 12 }] },
          { id: 13, name: 'Porto', code: 'PRT', countryId: 1, cities: [{ id: 120, name: 'Porto', stateId: 13 }, { id: 121, name: 'Vila Nova de Gaia', stateId: 13 }, { id: 122, name: 'Matosinhos', stateId: 13 }, { id: 178, name: 'Marco de Canaveses', stateId: 13 }] },
          { id: 14, name: 'Santarém', code: 'STR', countryId: 1, cities: [{ id: 130, name: 'Santarém', stateId: 14 }] },
          { id: 15, name: 'Setúbal', code: 'SET', countryId: 1, cities: [{ id: 140, name: 'Setúbal', stateId: 15 }, { id: 141, name: 'Almada', stateId: 15 }] },
          { id: 16, name: 'Viana do Castelo', code: 'VCT', countryId: 1, cities: [{ id: 150, name: 'Viana do Castelo', stateId: 16 }] },
          { id: 17, name: 'Vila Real', code: 'VRL', countryId: 1, cities: [{ id: 160, name: 'Vila Real', stateId: 17 }] },
          { id: 18, name: 'Viseu', code: 'VIS', countryId: 1, cities: [{ id: 170, name: 'Viseu', stateId: 18 }] },
          { id: 19, name: 'Açores', code: 'AZO', countryId: 1, cities: [{ id: 180, name: 'Ponta Delgada', stateId: 19 }] },
          { id: 20, name: 'Madeira', code: 'MAD', countryId: 1, cities: [{ id: 190, name: 'Funchal', stateId: 20 }] }
        ]
      },
      {
        id: 62,
        name: 'Espanha',
        code: 'ES',
        states: [
          { id: 100, name: 'Madrid', code: 'MAD', countryId: 62, cities: [{ id: 1000, name: 'Madrid', stateId: 100 }] },
          { id: 101, name: 'Barcelona', code: 'BCN', countryId: 62, cities: [{ id: 1010, name: 'Barcelona', stateId: 101 }] },
          { id: 102, name: 'Valencia', code: 'VAL', countryId: 62, cities: [{ id: 1020, name: 'Valencia', stateId: 102 }] },
          { id: 103, name: 'Sevilla', code: 'SEV', countryId: 62, cities: [{ id: 1030, name: 'Sevilla', stateId: 103 }] },
          { id: 104, name: 'Galicia', code: 'GAL', countryId: 62, cities: [{ id: 1040, name: 'Santiago de Compostela', stateId: 104 }, { id: 1041, name: 'Vigo', stateId: 104 }] }
        ]
      },
      {
        id: 69,
        name: 'França',
        code: 'FR',
        states: [
          { id: 200, name: 'Île-de-France', code: 'IDF', countryId: 69, cities: [{ id: 2000, name: 'Paris', stateId: 200 }] },
          { id: 201, name: 'Provence-Alpes-Côte d\'Azur', code: 'PAC', countryId: 69, cities: [{ id: 2010, name: 'Marseille', stateId: 201 }, { id: 2011, name: 'Nice', stateId: 201 }] },
          { id: 202, name: 'Nouvelle-Aquitaine', code: 'NAQ', countryId: 69, cities: [{ id: 2020, name: 'Bordeaux', stateId: 202 }] }
        ]
      },
      {
        id: 71,
        name: 'Reino Unido',
        code: 'GB',
        states: [
          { id: 300, name: 'England', code: 'ENG', countryId: 71, cities: [{ id: 3000, name: 'London', stateId: 300 }, { id: 3001, name: 'Manchester', stateId: 300 }, { id: 3002, name: 'Birmingham', stateId: 300 }] },
          { id: 301, name: 'Scotland', code: 'SCT', countryId: 71, cities: [{ id: 3010, name: 'Edinburgh', stateId: 301 }, { id: 3011, name: 'Glasgow', stateId: 301 }] },
          { id: 302, name: 'Wales', code: 'WLS', countryId: 71, cities: [{ id: 3020, name: 'Cardiff', stateId: 302 }] }
        ]
      },
      {
        id: 121,
        name: 'Luxemburgo',
        code: 'LU',
        states: [
          { id: 330, name: 'Diekirch', code: 'DIE', countryId: 121, cities: [{ id: 15160, name: 'Diekirch', stateId: 330 }, { id: 15165, name: 'Clervaux', stateId: 330 }] },
          { id: 331, name: 'Grevenmacher', code: 'GRE', countryId: 121, cities: [{ id: 15170, name: 'Grevenmacher', stateId: 331 }] },
          { id: 332, name: 'Luxembourg', code: 'LUX', countryId: 121, cities: [{ id: 15180, name: 'Luxembourg', stateId: 332 }] }
        ]
      },
      {
        id: 20,
        name: 'Bélgica',
        code: 'BE',
        states: [
          { id: 400, name: 'Brussels', code: 'BRU', countryId: 20, cities: [{ id: 4000, name: 'Brussels', stateId: 400 }] },
          { id: 401, name: 'Flanders', code: 'FLA', countryId: 20, cities: [{ id: 4010, name: 'Antwerp', stateId: 401 }, { id: 4011, name: 'Ghent', stateId: 401 }] },
          { id: 402, name: 'Wallonia', code: 'WAL', countryId: 20, cities: [{ id: 4020, name: 'Liège', stateId: 402 }] }
        ]
      },
      {
        id: 29,
        name: 'Brasil',
        code: 'BR',
        states: [
          { id: 500, name: 'São Paulo', code: 'SP', countryId: 29, cities: [{ id: 5000, name: 'São Paulo', stateId: 500 }] },
          { id: 501, name: 'Rio de Janeiro', code: 'RJ', countryId: 29, cities: [{ id: 5010, name: 'Rio de Janeiro', stateId: 501 }] },
          { id: 502, name: 'Minas Gerais', code: 'MG', countryId: 29, cities: [{ id: 5020, name: 'Belo Horizonte', stateId: 502 }] }
        ]
      }
    ];

    this.appState.setCountries(fallbackCountries);
    this.countriesLoaded = true;
  }

  /**
   * Get a country by ID
   */
  getCountryById(id: number): Country | undefined {
    return this.appState.countries().find(c => c.id === id);
  }

  /**
   * Get a state by ID within a country
   */
  getStateById(countryId: number, stateId: number): State | undefined {
    const country = this.getCountryById(countryId);
    return country?.states?.find(s => s.id === stateId);
  }

  /**
   * Get a city by ID within a state
   */
  getCityById(countryId: number, stateId: number, cityId: number): City | undefined {
    const state = this.getStateById(countryId, stateId);
    return state?.cities?.find(c => c.id === cityId);
  }
}
